import { Alert, Badge } from 'reactstrap';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import {
  useUserCommunication,
  useUserContacts,
  useUserSubUsers,
} from './useUsers';

/**
 * Operator-Read-Sicht auf Business-Account-Daten.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/business-accounts-admin.md (§
 *       "UX-Vorschlag — Operator-Frontend (Orbit)").
 *
 * Reine Lese-Tabs. Mutationen (Reset-Mail, Suspend) sind Phase 2 und
 * werden separat audit-loggt. Hier zeigen wir nur was der Org gehört
 * damit der Support einen Anruf beantworten kann.
 *
 * 404 vom Backend wird tolerant behandelt (Migration 022 evtl. noch
 * nicht in CORE deployed) — wir zeigen einen Hinweis statt einem Error.
 */

/* ── Sub-Users ─────────────────────────────────────────────────────── */

export function SubUsersTab({ userId, enabled }) {
  const query = useUserSubUsers(userId, { enabled });
  const list = query.data?.users ?? [];

  if (query.data?.unavailable) {
    return (
      <Alert color="info" className="small mb-0">
        Sub-User-Endpoint ist auf diesem CORE-Deploy nicht aktiv
        (<code>GET /v1/admin/users/:id/sub-users</code> → 404).
        Business-Accounts-Migration noch nicht durch.
      </Alert>
    );
  }

  return (
    <DataTable
      loading={query.isLoading}
      error={query.error}
      empty={!query.isLoading && list.length === 0}
      emptyMessage="Keine Sub-User."
      onRetry={() => query.refetch()}
    >
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th style={{ width: 130 }}>Rolle</th>
          <th style={{ width: 130 }}>Status</th>
          <th style={{ width: 150 }}>Letzter Login</th>
          <th style={{ width: 130 }}>Resource-Scope</th>
        </tr>
      </thead>
      <tbody>
        {list.map((u) => (
          <tr key={u.id}>
            <td className="small">{u.fullName ?? u.displayName ?? '—'}</td>
            <td className="small">{u.email ?? '—'}</td>
            <td>
              <Badge color="secondary" pill className="orbit-code-badge-sm">
                {u.role ?? '—'}
              </Badge>
            </td>
            <td>
              <Badge
                color={
                  u.status === 'active' ? 'success'
                  : u.status === 'invited' ? 'info'
                  : 'warning'
                }
                pill
              >
                {u.status ?? '—'}
              </Badge>
            </td>
            <td>
              {u.lastLoginAt
                ? <RelativeTime iso={u.lastLoginAt} />
                : <span className="text-muted">—</span>}
            </td>
            <td className="small">
              <ScopeSummary scopes={u.scopes} />
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}

function ScopeSummary({ scopes }) {
  if (!Array.isArray(scopes) || scopes.length === 0) {
    return <span className="text-muted">—</span>;
  }
  // {resource_type, mode, grantsCount} pro Eintrag — Spec §"Detail-Drawer"
  return (
    <div className="d-flex flex-column gap-1">
      {scopes.map((s) => (
        <span key={s.resource_type}>
          <code className="orbit-code-badge-sm">{s.resource_type}</code>{' '}
          <Badge color={s.mode === 'all' ? 'info' : 'warning'} pill className="orbit-code-badge-sm">
            {s.mode === 'granted_only' && s.grantsCount != null
              ? `granted: ${s.grantsCount}`
              : s.mode}
          </Badge>
        </span>
      ))}
    </div>
  );
}

/* ── Contacts ──────────────────────────────────────────────────────── */

export function ContactsTab({ userId, enabled }) {
  const query = useUserContacts(userId, { enabled });
  const list = query.data?.contacts ?? [];

  if (query.data?.unavailable) {
    return (
      <Alert color="info" className="small mb-0">
        Contacts-Endpoint ist nicht aktiv
        (<code>GET /v1/admin/users/:id/contacts</code> → 404).
      </Alert>
    );
  }

  return (
    <DataTable
      loading={query.isLoading}
      error={query.error}
      empty={!query.isLoading && list.length === 0}
      emptyMessage="Keine Ansprechpartner."
      onRetry={() => query.refetch()}
    >
      <thead>
        <tr>
          <th>Name</th>
          <th>Rolle / Position</th>
          <th>Email</th>
          <th>Telefon</th>
          <th style={{ width: 140 }}>Erstellt</th>
        </tr>
      </thead>
      <tbody>
        {list.map((c) => (
          <tr key={c.id}>
            <td className="small">{c.fullName ?? c.name ?? '—'}</td>
            <td className="small">{c.role ?? c.position ?? '—'}</td>
            <td className="small">{c.email ?? <span className="text-muted">—</span>}</td>
            <td className="small">{c.phone ?? <span className="text-muted">—</span>}</td>
            <td>
              {c.createdAt
                ? <RelativeTime iso={c.createdAt} />
                : <span className="text-muted">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}

/* ── Communication ─────────────────────────────────────────────────── */

export function CommunicationTab({ userId, enabled }) {
  const query = useUserCommunication(userId, { enabled });
  const list = query.data?.channels ?? [];

  if (query.data?.unavailable) {
    return (
      <Alert color="info" className="small mb-0">
        Communication-Endpoint ist nicht aktiv
        (<code>GET /v1/admin/users/:id/communication</code> → 404).
      </Alert>
    );
  }

  return (
    <DataTable
      loading={query.isLoading}
      error={query.error}
      empty={!query.isLoading && list.length === 0}
      emptyMessage="Keine Kommunikationskanäle."
      onRetry={() => query.refetch()}
    >
      <thead>
        <tr>
          <th style={{ width: 100 }}>Typ</th>
          <th>Wert</th>
          <th style={{ width: 110 }}>Zweck</th>
          <th style={{ width: 100 }}>Verifiziert</th>
          <th style={{ width: 140 }}>Hinzugefügt</th>
        </tr>
      </thead>
      <tbody>
        {list.map((c) => (
          <tr key={c.id}>
            <td>
              <Badge color="secondary" pill className="orbit-code-badge-sm">
                {c.kind ?? c.type ?? '—'}
              </Badge>
            </td>
            <td className="small font-monospace">{c.value ?? '—'}</td>
            <td className="small">{c.purpose ?? '—'}</td>
            <td>
              {c.verifiedAt
                ? <Badge color="success" pill className="orbit-code-badge-sm">✓ {new Date(c.verifiedAt).toLocaleDateString('de-DE')}</Badge>
                : <Badge color="warning" pill className="orbit-code-badge-sm">offen</Badge>}
            </td>
            <td>
              {c.createdAt
                ? <RelativeTime iso={c.createdAt} />
                : <span className="text-muted">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}
